// [2026-09-05] ADDED — the table furniture the rest of the InCruiter product uses: a numbered
// pager with ellipses, sortable column headers, and a client-side page source.
//
// PORTED, NOT INVENTED. `usePagination` is frontend-account/src/utils/usePagination.jsx with its
// logic unchanged — same siblingCount, same `totalPageNumbers = siblingCount + 5`, same four
// branches, same DOTS sentinel. The markup and classes follow
// frontend-account/src/component/CommonComponent/Pagination.jsx and
// assets/scss/components/_pagination.scss (32px pills, radius 16, #e9e9e9 on #6f6f84, selected in
// $theme-blue). Two products that paginate differently look like two products.

import { useMemo } from 'react'
import twoArrows from '../../assets/2arrows.svg'

export const DOTS = '...'

const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) => i + start)

/** frontend-account/src/utils/usePagination.jsx, verbatim in behaviour.
 *
 *  Returns the page numbers to render, with DOTS where the run is broken. The one thing worth
 *  knowing if you change it: the branch count is chosen so the control NEVER CHANGES WIDTH as you
 *  page through — a pager that reflows under the cursor makes you miss the button you aimed at. */
export function usePagination({ totalCount, pageSize, siblingCount = 1, currentPage }) {
  return useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize)
    // siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5

    if (totalPageNumbers >= totalPageCount) return range(1, totalPageCount)

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2

    if (!shouldShowLeftDots && shouldShowRightDots) {
      return [...range(1, 3 + 2 * siblingCount), DOTS, totalPageCount]
    }
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const count = 3 + 2 * siblingCount
      return [1, DOTS, ...range(totalPageCount - count + 1, totalPageCount)]
    }
    if (shouldShowLeftDots && shouldShowRightDots) {
      return [1, DOTS, ...range(leftSiblingIndex, rightSiblingIndex), DOTS, totalPageCount]
    }
    // totalPageCount is 0 (an empty result set) — render nothing rather than a lone page 1.
    return []
  }, [totalCount, pageSize, siblingCount, currentPage])
}

/** The numbered pager. `page` is 1-based, matching the account app's component. */
export function PfPager({ page, pageSize, total, onPageChange }) {
  const pages = usePagination({ currentPage: page, pageSize, totalCount: total })
  const lastPage = pages.length ? pages[pages.length - 1] : 1
  if (total === 0) return null

  return (
    <ul className="pf-pagination">
      <li className={`pf-page-item${page === 1 ? ' disabled' : ''}`}
          onClick={() => page > 1 && onPageChange(page - 1)}
          role="button" tabIndex={page === 1 ? -1 : 0} aria-label="Previous page"
          onKeyDown={(e) => { if (e.key === 'Enter' && page > 1) onPageChange(page - 1) }}>
        <span aria-hidden="true">‹</span>
      </li>
      {pages.map((n, i) => (n === DOTS ? (
        <li className="pf-page-item dots" key={`d${i}`} aria-hidden="true">…</li>
      ) : (
        <li key={n} className={`pf-page-item${page === n ? ' selected' : ''}`}
            onClick={() => onPageChange(n)}
            role="button" tabIndex={0}
            aria-label={`Page ${n}`} aria-current={page === n ? 'page' : undefined}
            onKeyDown={(e) => { if (e.key === 'Enter') onPageChange(n) }}>
          {n}
        </li>
      )))}
      <li className={`pf-page-item${page === lastPage ? ' disabled' : ''}`}
          onClick={() => page < lastPage && onPageChange(page + 1)}
          role="button" tabIndex={page === lastPage ? -1 : 0} aria-label="Next page"
          onKeyDown={(e) => { if (e.key === 'Enter' && page < lastPage) onPageChange(page + 1) }}>
        <span aria-hidden="true">›</span>
      </li>
    </ul>
  )
}

/** A sortable column header — the label plus the account app's own 2arrows.svg.
 *
 *  RENDERS AS PLAIN TEXT WHEN `onSort` IS ABSENT.
 *
 *  [2026-09-05] CORRECTED. This comment previously said the API took no sort parameter and that
 *  header arrows on the two server-paginated tables would therefore be a lie. That WAS true and is
 *  no longer: GET /me/requests and GET /me/sessions now accept `order` (and a date range), added in
 *  the same change as the filter panel. Ordering on those two is exposed through the panel's
 *  "Order By", which drives the server query, rather than through per-column arrows — the endpoints
 *  order by their timestamp (and reference_id for sessions), not by an arbitrary column, so a
 *  clickable header on every column would still promise more than the API delivers.
 *
 *  Per-column arrows remain enabled only where the browser holds the WHOLE result set, which is
 *  what makeClientPager guarantees. */
export function PfSortHeader({ label, sortKey, sort, onSort, align }) {
  if (!onSort) return <span>{label}</span>
  const active = sort?.key === sortKey
  const dir = active ? sort.dir : null
  return (
    <button type="button"
            className={`pf-sort${active ? ' active' : ''}`}
            style={align === 'right' ? { marginLeft: 'auto' } : undefined}
            aria-label={`Sort by ${label}${dir ? `, currently ${dir}ending` : ''}`}
            onClick={() => onSort(sortKey, active && dir === 'asc' ? 'desc' : 'asc')}>
      {label}
      <img src={twoArrows} alt="" aria-hidden="true"
           className={dir ? `dir-${dir}` : undefined} />
    </button>
  )
}

/** Turn an in-memory array into the `fetchPage` DataTable already speaks.
 *
 *  WHY THIS EXISTS. DataTable's search, filters, page-size, reset-to-page-1 and pager are written
 *  once and already correct. Two screens — the credit ledger and the team list — receive their
 *  whole result set in ONE response, so they need the same furniture over local data rather than a
 *  second, subtly different table. This adapts one to the other instead of duplicating it.
 *
 *  Sorting is honest here for exactly the reason it is refused in DataTable's own headers: this
 *  function is given every row, so ordering them orders the whole set and not a visible slice.
 *
 *  `searchIn` maps a row to the text a search should match. `filterFns` maps a filter key to a
 *  predicate factory. Both optional.
 */
export function makeClientPager(rows, { searchIn, filterFns = {}, dateOf, nameOf } = {}) {
  return async ({ limit, offset, search, sortKey, sortDir, dateFrom, dateTo, order, ...filters }) => {
    let out = rows || []

    // [2026-09-05] ADDED — the filter panel's date range and Order By, applied locally.
    //
    // The upper bound is the day AFTER `dateTo`, matching the server's _day_range: a range of
    // 1st–7th must include everything ON the 7th, and comparing against the bare date would cut at
    // 00:00 and silently drop that day. Both ends compare as date-only text, which is exact for
    // ISO-8601 and needs no timezone decision.
    if (dateOf && (dateFrom || dateTo)) {
      const upper = dateTo
        ? new Date(new Date(`${dateTo}T00:00:00Z`).getTime() + 86400000).toISOString().slice(0, 10)
        : null
      out = out.filter((row) => {
        const day = String(dateOf(row) || '').slice(0, 10)
        if (!day) return false
        if (dateFrom && day < dateFrom) return false
        if (upper && day >= upper) return false
        return true
      })
    }

    for (const [key, value] of Object.entries(filters)) {
      if (!value || !filterFns[key]) continue
      out = out.filter(filterFns[key](value))
    }

    if (search && searchIn) {
      const term = String(search).toLowerCase()
      out = out.filter((row) => String(searchIn(row) || '').toLowerCase().includes(term))
    }

    if (sortKey) {
      // Copied before sorting: Array.prototype.sort mutates, and `rows` is the caller's state.
      // Sorting it in place would reorder the array React is holding without a re-render, so the
      // NEXT unrelated update would repaint in a different order for no visible reason.
      out = [...out].sort((a, b) => {
        const x = a?.[sortKey]
        const y = b?.[sortKey]
        // Missing values sort last in both directions — never above real data, which is what a
        // naive comparator does with undefined and which makes an empty column look like the top
        // result.
        if (x == null && y == null) return 0
        if (x == null) return 1
        if (y == null) return -1
        const cmp = typeof x === 'number' && typeof y === 'number'
          ? x - y
          : String(x).localeCompare(String(y), undefined, { numeric: true })
        return sortDir === 'desc' ? -cmp : cmp
      })
    }

    // Order By, from the panel. It is applied AFTER any column sort so that an explicit click on a
    // header still wins over the panel's default — the more specific gesture should not be silently
    // overridden by a setting the person may have chosen minutes ago.
    if (order && !sortKey) {
      if (order === 'name' && nameOf) {
        out = [...out].sort((a, b) =>
          String(nameOf(a) || '').localeCompare(String(nameOf(b) || ''), undefined, { numeric: true }))
      } else if (dateOf) {
        out = [...out].sort((a, b) => {
          const x = String(dateOf(a) || ''), y = String(dateOf(b) || '')
          return order === 'asc' ? x.localeCompare(y) : y.localeCompare(x)
        })
      }
    }

    return { rows: out.slice(offset, offset + limit), total: out.length }
  }
}
